/* eslint-disable react-refresh/only-export-components -- arquivo mistura componente com hooks/types/helpers; refactor pra split é cosmético (HMR fast-refresh). */
import { createContext, useContext, useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useCampaigns } from '@/features/campaigns/hooks/use-campaigns'
import { useClientContext } from '@/contexts/ClientContext'
import { useAuth } from '@/contexts/AuthContext'
import type { Campaign } from '@/types'

interface CampaignContextType {
  /** Todas as campanhas visíveis ao user (já filtradas por RLS no banco). */
  allCampaigns: Campaign[]
  /** Campanhas filtradas pelo cliente ativo do shell. Quando "Todos clientes",
   *  igual a allCampaigns. */
  campaigns: Campaign[]
  /** Campanha selecionada. NULL = "Todas as campanhas" (sem filtro de campanha). */
  selectedCampaign: Campaign | null
  /** Passa string ou null. null = limpar seleção (ver todas). */
  setSelectedCampaignId: (id: string | null) => void
  loading: boolean
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined)

const STORAGE_KEY = 'runna_selected_campaign'
/** Sentinela em localStorage que significa "explicitamente todas". Diferente
 *  de ausente (primeira visita), pra não sobrescrever escolha do user com auto-select. */
const ALL_CAMPAIGNS = 'all'

export function CampaignProvider({ children }: { children: ReactNode }) {
  const { campaigns: allCampaigns, loading } = useCampaigns()
  const { activeClientId, isLocked: isClientUser } = useClientContext()
  const { profile } = useAuth()

  // 3 estados possíveis: id da campanha | null (default - será auto-resolvido) | 'all' (explicit)
  const [savedRaw, setSavedRaw] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY) // pode ser null, uuid, ou 'all'
  })

  // Campanhas filtradas pelo cliente ativo
  const campaigns = useMemo(() => {
    if (!activeClientId) return allCampaigns
    return allCampaigns.filter(c => c.client_id === activeClientId)
  }, [allCampaigns, activeClientId])

  // Resolve estado: 'all' → null (filtro vazio), uuid válido → uuid, else → auto-select.
  // Auto-select acontece SÓ para client_user (cliente externo trabalha campanha por
  // campanha — faz sentido focar uma sempre) ou quando há um cliente ativo filtrado
  // (operador da agência focou um cliente: foca a primeira campanha dele).
  // Para team_member em "Todos os clientes", o default é null (vê tudo).
  const selectedId = useMemo(() => {
    if (savedRaw === ALL_CAMPAIGNS) return null
    if (savedRaw && campaigns.some(c => c.id === savedRaw)) return savedRaw
    // savedRaw inválido ou ausente: auto-select?
    if (campaigns.length === 0) return null
    const isTeamMemberInAllClients = !isClientUser && !activeClientId
    if (isTeamMemberInAllClients) return null // team_member em "Todos" → sem filtro de campanha
    // Cliente externo OU operador focado num cliente → auto-select primeira ativa
    const active = campaigns.find(c => c.status === 'active')
    return active?.id || campaigns[0].id
  }, [savedRaw, campaigns, isClientUser, activeClientId])

  // Persiste o resultado da auto-resolução em localStorage (sincronização legítima
  // entre savedRaw e o id resolvido: senão na próxima renderização re-executa o useMemo
  // e perde a escolha implícita).
  useEffect(() => {
    if (selectedId && selectedId !== savedRaw) {
      localStorage.setItem(STORAGE_KEY, selectedId)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSavedRaw(selectedId)
    }
  }, [selectedId, savedRaw])

  // Reset campanha SÓ na transição "cliente específico → Todos os clientes".
  // Usar useRef pra detectar transição — useEffect simples dispararia sempre que
  // activeClientId estivesse null, sobrescrevendo a escolha manual de campanha.
  const prevClientIdRef = useRef(activeClientId)
  useEffect(() => {
    if (!profile) return
    if (isClientUser) return
    const prev = prevClientIdRef.current
    prevClientIdRef.current = activeClientId
    // Transição: tinha cliente específico → agora é null (Todos)
    if (prev !== null && activeClientId === null) {
      localStorage.setItem(STORAGE_KEY, ALL_CAMPAIGNS)
      setSavedRaw(ALL_CAMPAIGNS)
    }
  }, [activeClientId, isClientUser, profile])

  const handleSetSelectedId = (id: string | null) => {
    if (id === null) {
      localStorage.setItem(STORAGE_KEY, ALL_CAMPAIGNS)
      setSavedRaw(ALL_CAMPAIGNS)
    } else {
      localStorage.setItem(STORAGE_KEY, id)
      setSavedRaw(id)
    }
  }

  const selectedCampaign = selectedId
    ? campaigns.find(c => c.id === selectedId) || null
    : null

  return (
    <CampaignContext.Provider value={{
      allCampaigns, campaigns, selectedCampaign, setSelectedCampaignId: handleSetSelectedId, loading,
    }}>
      {children}
    </CampaignContext.Provider>
  )
}

export function useCampaignContext() {
  const context = useContext(CampaignContext)
  if (!context) throw new Error('useCampaignContext must be used within CampaignProvider')
  return context
}
